import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Button,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InlineLoading,
  TextInput,
  Form
} from 'carbon-components-react';
import ReactGA from 'react-ga';
import { currentUser } from 'redux/session/selectors';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { userPlanUpgradeStart } from './localStore/actions';
import { useFormik } from 'formik';
import { FormError } from 'components';
import { PlanUpgradeModalSchema } from 'utilities/validation/planUpgradeModal.validation';
import { upperFirst } from 'utilities/helper.util';

interface FormValues {
  contactNumber: string;
}

const UpgradeModal: React.FC<Props> = ({
  isModalOpen,
  setIsModalOpen,
  source
}) => {
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { upgradePlanResponse, submittingUpgrade, upgradePlanError } =
    localStore;
  const user = useSelector(currentUser);
  const [upgradeRequested, setUpgradeRequested] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preview = queryParams.get('preview');

  const formInitialValues = {
    contactNumber: user.mobile_number
  };

  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleSubmit,
    handleBlur,
    setFieldTouched
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema: PlanUpgradeModalSchema,
    onSubmit: (values: FormValues) => {
      handleModalSubmit(values);
    }
  });

  const handleModalCancel = useCallback(() => {
    ReactGA.event({
      category: 'Account',
      action: 'Cancelled Upgrade my plan form'
    });
    setFieldValue('contactNumber', user.mobile_number, true);
    setIsModalOpen(false);
  }, [setIsModalOpen, setFieldValue, user]);

  const handleTextInputChange = (key: string, val: string) => {
    if (val.match(/^[0-9- ]+$/) || val === '') {
      setFieldTouched(key, true, true);
      setFieldValue(key, val, true);
    }
  };

  const handleModalSubmit = (values: FormValues) => {
    let from = '';
    if (!preview && !source) {
      const { pathname } = window.location;

      if (pathname.match(/account$/)) {
        from = 'Account';
      } else if (pathname.match(/settings$/)) {
        from = 'Settings';
      } else if (pathname.match(/mailboxes$/)) {
        from = 'Mailbox';
      } else if (pathname.match(/enquiries\/new$/)) {
        from = 'Create';
      } else if (pathname.match(/\/$/)) {
        from = 'Home';
      } else if (pathname.match(/r2/)) {
        from = 'Home';
      } else {
        from = pathname;
      }
    } else if (source) {
      from = source;
    } else {
      from = preview || 'Unknwon';
    }
    const data = {
      contactNumber: values.contactNumber,
      from: upperFirst(from)
    };
    ReactGA.event({
      category: 'Account',
      action: 'Submitted Upgrade my plan form'
    });
    localDispatch(userPlanUpgradeStart(data));
  };

  useEffect(() => {
    if (!submittingUpgrade && (upgradePlanResponse || upgradePlanError)) {
      setUpgradeRequested(true);
      setTimeout(() => {
        setUpgradeRequested(false);
        handleModalCancel();
      }, 2000);
    }
  }, [
    upgradePlanResponse,
    submittingUpgrade,
    upgradePlanError,
    setIsModalOpen,
    handleModalCancel
  ]);

  useEffect(() => {
    if (Object.keys(errors).length <= 0 && values.contactNumber) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }, [errors, values]);

  useEffect(() => {
    isModalOpen && ReactGA.modalview('/upgrade-my-plan');
  }, [isModalOpen]);

  return (
    <div id="UpgradeModal" data-testid="UpgradeModal" className="upgrade-modal">
      <ComposedModal
        className="upgrade-modal"
        data-testid="feedbackModal"
        open={isModalOpen}
        onClose={handleModalCancel}
        preventCloseOnClickOutside={true}
      >
        <ModalHeader>
          <div className="upgrade-modal-header-title">FREE PLAN</div>
        </ModalHeader>
        <Form className="modal-form--wrapper" onSubmit={handleSubmit}>
          <ModalBody>
            <div className="upgrade-modal-body-title">Upgrade my plan</div>
            <div className="upgrade-modal-body-content">
              Thanks for your interest in a plan upgrade! Just submit a request
              and one of our consultants will be in touch soon.
            </div>
            <div className="upgrade-modal-body-contact-number">
              <TextInput
                id="contactNumber"
                name="contactNumber"
                labelText="Your contact number"
                placeholder=""
                maxLength={16}
                onBlur={handleBlur}
                onChange={(val) =>
                  handleTextInputChange('contactNumber', val.target.value)
                }
                value={values?.contactNumber || ''}
              />
              {errors.contactNumber && touched.contactNumber && (
                <FormError>{errors.contactNumber}</FormError>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              kind="tertiary"
              onClick={handleModalCancel}
              data-testid="Cancel"
            >
              Cancel
            </Button>
            <Button
              kind="secondary"
              type="submit"
              data-testid="submit"
              disabled={
                isSubmitDisabled || upgradeRequested || submittingUpgrade
              }
            >
              {!submittingUpgrade && !upgradeRequested ? (
                'Submit request'
              ) : (
                <InlineLoading
                  description={
                    !submittingUpgrade ? 'Submitted' : 'Submitting...'
                  }
                  status={!submittingUpgrade ? 'finished' : 'active'}
                  aria-live={'assertive'}
                />
              )}
            </Button>
          </ModalFooter>
        </Form>
      </ComposedModal>
    </div>
  );
};

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: Function;
  source?: string;
};

export default React.memo(UpgradeModal);
